// Contact-channel deep links + first-message intent builders.
//
// The point: on WhatsApp world chat, the first message a seller reads is
// "hi bhai rate kya hai" and it takes 15 back-and-forth messages to
// establish what the buyer actually needs. If we send them to WhatsApp
// with the requirement already typed, the seller replies with a rate on
// message #1 instead of message #12.
//
// Only WhatsApp reliably supports pre-filled text via the `?text=` param
// on wa.me. Telegram / Discord / phone get the raw handle; the caller
// can additionally copy the intent to the clipboard on click.

import { compactNumber, formatFlatPrice, formatRatePer1k } from "./format";
import { SEGMENT_KEYS, SEGMENTS } from "../services/constants";

// Base URL of the pop-market site — used inside the intent text so the
// receiver can jump straight back to the source profile.
function siteOrigin() {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/\/$/, "");
  }
  return "https://popmarket.playaware.gg";
}

// Trim the intent to keep the WhatsApp opener readable on mobile. Long
// pre-fills push the "send" affordance below the fold and viewers edit
// them down before sending — defeats the point.
const MAX_INTENT_CHARS = 320;
function fit(s) {
  return s.length <= MAX_INTENT_CHARS ? s : s.slice(0, MAX_INTENT_CHARS - 1) + "…";
}

// Pick the segment worth mentioning in the opener. Prefer the segment
// with the highest quantity/stock; ties broken by SEGMENT_KEYS order.
function topSegment(map) {
  if (!map) return null;
  let best = null;
  for (const key of SEGMENT_KEYS) {
    const qty = Number(map[key]?.quantity ?? map[key]) || 0;
    if (qty <= 0) continue;
    if (!best || qty > best.qty) best = { key, qty };
  }
  return best;
}

function segmentLabel(key) {
  return SEGMENTS.find((s) => s.key === key)?.label || key;
}

// Buyer → seller opener. Viewer is on Storefront looking at `seller`.
// Optional `viewer` = { handle } if signed in as a pop-market user.
export function buildBuyerToSellerIntent(seller, viewer = null) {
  if (!seller) return "";
  const top = topSegment(seller.stock);
  const rate = seller.rates?.mix?.base ?? seller.rates?.[top?.key]?.base;
  const rateStr = rate != null
      ? (top?.key === "lumen" || top?.key === "helicopter"
          ? formatFlatPrice(rate, seller.currency)
          : formatRatePer1k(rate, seller.currency)) : null;

  const parts = [];
  parts.push(viewer?.handle
      ? `Hi @${seller.handle} — this is @${viewer.handle} from PlayAware Pop Market.`
      : `Hi @${seller.handle} — saw your PlayAware Pop Market listing.`);

  const bits = [];
  bits.push(seller.game);
  if (top) bits.push(`${segmentLabel(top.key)} ${compactNumber(top.qty)} in stock`);
  if (rateStr) bits.push(`from ${rateStr}`);
  if (bits.length) parts.push(`(${bits.join(" · ")})`);

  parts.push("Interested — what's your best rate today?");
  parts.push(`— ${siteOrigin()}/seller/${seller.handle}`);
  return fit(parts.join(" "));
}

// Seller → buyer opener. Viewer is on BuyerProfile looking at `buyer`.
export function buildSellerToBuyerIntent(buyer, viewer = null) {
  if (!buyer) return "";
  const need = topSegment(buyer.needs);
  const n = need ? buyer.needs[need.key] : null;
  const perK = need?.key === "mix" || need?.key === "premium";

  const parts = [];
  parts.push(viewer?.handle
      ? `Hi @${buyer.handle} — this is @${viewer.handle} from PlayAware Pop Market.`
      : `Hi @${buyer.handle} — saw your PlayAware Pop Market requirement.`);

  const bits = [];
  bits.push(buyer.game);
  if (need && n) {
    const q = compactNumber(n.quantity);
    const rate = Number(n.maxRate || 0);
    const rateStr = rate > 0
        ? `≤ ₹${rate.toFixed(rate >= 100 ? 0 : 2)}${perK ? "/k" : ""}` : null;
    bits.push(`need ${q} ${segmentLabel(need.key)}${rateStr ? " " + rateStr : ""}`);
    if (n.urgency) bits.push(String(n.urgency).toUpperCase());
  }
  if (bits.length) parts.push(`(${bits.join(" · ")})`);

  parts.push("I have stock — reply if you want a quote.");
  parts.push(`— ${siteOrigin()}/buyer/${buyer.handle}`);
  return fit(parts.join(" "));
}

// contactHref(channel, value, intent?)
//   channel  — "whatsapp" | "telegram" | "phone" | "discord"
//   value    — the raw handle/number returned by revealContact()
//   intent   — optional pre-filled first message (WhatsApp only supports it
//              natively; other channels ignore it here — the caller can
//              still copy it to the clipboard on click)
export function contactHref(channel, value, intent = "") {
  const raw = String(value || "");
  if (channel === "whatsapp") {
    const digits = raw.replace(/[^0-9]/g, "");
    if (!digits) return undefined;
    const text = intent ? `?text=${encodeURIComponent(intent)}` : "";
    return `https://wa.me/${digits}${text}`;
  }
  if (channel === "telegram") {
    const u = raw.replace(/^@/, "");
    return `https://t.me/${u}`;
  }
  if (channel === "phone") return `tel:${raw}`;
  return undefined;
}

// Best-effort clipboard write. Silently ignores failures — the caller
// will still show the raw handle so the user can copy manually.
export function copyIntent(intent) {
  if (!intent || typeof navigator === "undefined") return Promise.resolve(false);
  const clip = navigator.clipboard;
  if (!clip || !clip.writeText) return Promise.resolve(false);
  return clip.writeText(intent).then(() => true).catch(() => false);
}
